import { useEffect, useMemo, useRef } from 'react';

import { isEqual } from 'lodash';
import { useInterval } from 'react-use';

import { apiFactory, YacsTopicKey } from '@app/client';
import { type ExchangeRates, useExchangeRatesQuery, useExchangeRatesStore } from '@app/store/zustand';

const CONVERSION_RATE_SUBSCRIPTION = 'conversionRateEvent';

type Currency = 'ETH' | 'MATIC' | 'APT' | 'USD';

type CurrencyConversionEvent = {
    from: Currency;
    market: string;
    price: number;
    quantity: number;
    timestamp: number;
    to: Currency;
};

interface CurrencyConversionWsProps {
    disabled?: boolean;
}
export function useCurrencyConversionWs(props?: CurrencyConversionWsProps) {
    const { disabled = false } = props || {};
    const { rates } = useExchangeRatesQuery({ disabled });
    const setRates = useExchangeRatesStore((state) => state.setRates);
    const currentStoredRates = useExchangeRatesStore((state) => state.rates);
    const ratesRef = useRef<ExchangeRates>();

    useEffect(() => {
        if (rates && !ratesRef.current) {
            if (!isEqual(rates, currentStoredRates)) {
                setRates(rates);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rates]);

    useInterval(() => {
        const current = ratesRef.current;
        if (current && !isEqual(current, currentStoredRates)) {
            setRates(current);
        }
    }, 2000);

    const yacsWsConnection = useMemo(() => {
        if (disabled) return;
        const connection = apiFactory().websocketConnections.yacs.connection;
        return connection?.on('connect', () => {
            return connection;
        });
    }, [disabled]);

    useEffect(() => {
        const subscription = CONVERSION_RATE_SUBSCRIPTION;
        const topicName = `conversion_rate_event.*`;
        const handleOnEventChange = (event: CurrencyConversionEvent) => {
            if (event.to === 'USD') {
                if (ratesRef.current) {
                    ratesRef.current[event.from.toLowerCase()] = event.price;
                } else {
                    ratesRef.current = { ...currentStoredRates, [event.from.toLowerCase()]: event.price };
                }
            }
        };

        if (disabled) {
            yacsWsConnection?.emit(YacsTopicKey.unsubscribe, topicName);
            yacsWsConnection?.off();
            return;
        }

        if (yacsWsConnection) {
            yacsWsConnection?.emit(YacsTopicKey.subscribe, topicName);
            yacsWsConnection?.on(subscription, async (data) => {
                const eventData = data as CurrencyConversionEvent;
                if (eventData) {
                    handleOnEventChange(eventData);
                }
            });
        }

        return () => {
            yacsWsConnection?.emit(YacsTopicKey.unsubscribe, topicName);
            yacsWsConnection?.off();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled, yacsWsConnection]);
}
